import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LTButton, LTInputBox, LTText } from '@lambdatestincprivate/lt-components';
import { AlertFillIcon, FileZipIcon } from '@primer/octicons-react';
import { useWindowFileDrop } from '../hooks/useWindowFileDrop';
import { EvidenceIntro } from './EvidenceIntro';
import styles from './landing.module.css';

/** A file is openable here only if it's a sealed pack or an artifact zip;
 * anything else gets the inline "not an evidence pack" error. */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function isOpenable(name) {
  var lower = name.toLowerCase();
  return lower.endsWith('.evidence') || lower.endsWith('.zip');
}

/**
 * Shown when there is no ?pack= param: a drop zone (drag anywhere on the
 * page), a file picker, or a pasted pack URL — wrapped in the shared
 * EvidenceIntro. While a file is dragged over the window the drop target
 * lights up and the rest of the page dims; a wrong file type shows an inline
 * error in the drop zone instead of opening.
 */
export function Landing(_ref) {
  var onFile = _ref.onFile,
    onUrl = _ref.onUrl;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    url = _useState2[0],
    setUrl = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    rejected = _useState4[0],
    setRejected = _useState4[1];
  var inputRef = useRef(null);
  var handleFile = useCallback(function (file) {
    if (isOpenable(file.name)) {
      setRejected(null);
      onFile(file);
    } else {
      setRejected(file.name);
    }
  }, [onFile]);
  var dragging = useWindowFileDrop(handleFile);
  // A fresh drag clears any previous rejection so the blue drop state shows
  // cleanly instead of the red error.
  useEffect(function () {
    if (dragging) setRejected(null);
  }, [dragging]);
  var onPick = useCallback(function (e) {
    var _e$target$files;
    var file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) handleFile(file);
    e.target.value = '';
  }, [handleFile]);
  var openUrl = useCallback(function () {
    var trimmed = url.trim();
    if (trimmed) onUrl(trimmed);
  }, [onUrl, url]);
  var submitUrl = useCallback(function (e) {
    e.preventDefault();
    openUrl();
  }, [openUrl]);
  var showError = !dragging && rejected !== null;
  var title;
  var subtitle;
  var buttonLabel;
  if (dragging) {
    title = 'Drop to open';
    subtitle = 'Release anywhere on this page — it opens right here';
    buttonLabel = null;
  } else if (rejected) {
    title = 'This file isn’t an evidence pack';
    subtitle = "\u201C".concat(rejected, "\u201D can\u2019t be opened here");
    buttonLabel = 'Choose another file';
  } else {
    title = 'Drop your .evidence file here';
    subtitle = 'or choose one from your computer';
    buttonLabel = 'Choose file';
  }
  var dropZoneCls = [styles.dropZone, dragging ? styles.active : '', showError ? styles.error : ''].filter(Boolean).join(' ');
  return /*#__PURE__*/_jsxs(EvidenceIntro, {
    dim: dragging,
    children: [/*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs("div", {
        className: dropZoneCls,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.dropIcon,
          "aria-hidden": "true",
          children: /*#__PURE__*/_jsx(FileZipIcon, {
            size: 28
          })
        }), /*#__PURE__*/_jsx(LTText, {
          as: "p",
          text: title,
          variant: LTText.TextVariant.SUBHEADER_BOLD,
          color: "var(--lt-text-primary)",
          style: {
            margin: 0
          }
        }), /*#__PURE__*/_jsx(LTText, {
          as: "p",
          text: subtitle,
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: showError ? 'var(--lt-text-muted)' : 'var(--lt-text-secondary)',
          style: {
            margin: 0
          }
        }), buttonLabel && /*#__PURE__*/_jsx("span", {
          className: styles.chooseBtn,
          children: /*#__PURE__*/_jsx(LTButton, {
            label: buttonLabel,
            variant: LTButton.Variant.DEFAULT,
            size: LTButton.Size.MEDIUM,
            onClick: function onClick() {
              var _inputRef$current;
              return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.click();
            }
          })
        })]
      }), showError && /*#__PURE__*/_jsxs("div", {
        className: styles.errorNote,
        role: "alert",
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.errorNoteIcon,
          "aria-hidden": "true",
          children: /*#__PURE__*/_jsx(AlertFillIcon, {
            size: 14
          })
        }), /*#__PURE__*/_jsx(LTText, {
          as: "span",
          text: "Evidence Viewer opens .evidence files, or the .zip you download from a GitHub Actions run.",
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-error)"
        })]
      })]
    }), /*#__PURE__*/_jsx("input", {
      ref: inputRef,
      type: "file",
      accept: ".evidence,.zip,application/zip",
      className: "ev-sr-only",
      "aria-label": "Choose a .evidence file",
      onChange: onPick
    }), /*#__PURE__*/_jsxs("div", {
      className: dragging ? styles.dimmed : '',
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.divider,
        children: /*#__PURE__*/_jsx(LTText, {
          as: "span",
          text: "or open from a link",
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-muted)"
        })
      }), /*#__PURE__*/_jsxs("form", {
        className: styles.urlForm,
        onSubmit: submitUrl,
        style: {
          marginTop: '12px'
        },
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.urlField,
          children: /*#__PURE__*/_jsx(LTInputBox, {
            type: "text",
            inputMode: "url",
            inputOnly: true,
            name: "pack-url",
            ariaLabel: "Pack URL",
            placeholder: "Paste a pack URL or a GitHub artifact link",
            size: LTInputBox.Size.MEDIUM,
            variant: LTInputBox.Variant.WHITE,
            value: url,
            onChange: function onChange(value) {
              return setUrl(value);
            },
            trailingAction: function trailingAction() {
              return setUrl('');
            }
          })
        }), /*#__PURE__*/_jsx(LTButton, {
          label: "Open",
          variant: LTButton.Variant.DEFAULT,
          size: LTButton.Size.MEDIUM,
          onClick: openUrl
        })]
      })]
    })]
  });
}